import 'katex/dist/katex.min.css';
import React, { FC } from 'react';
import { Editor, useEditorState } from '@tiptap/react';
import { AddGuideLinkToolbarButton } from './extensions/toolbar/AddGuideLinkToolbarButton';
import { AddCitationLinkToolbarButton } from './extensions/toolbar/AddCitationLinkToolbarButton';
import {
  MenuButtonBold,
  MenuButtonBulletedList,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuButtonUnderline,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
  MenuSelectTextAlign,
  LinkBubbleMenu,
  MenuButtonEditLink,
  MenuButtonSuperscript,
  MenuButtonSubscript,
  RichTextField,
  RichTextEditorProvider,
  MenuButtonUndo,
  MenuButtonRedo,
  MenuButtonHighlightToggle,
} from 'mui-tiptap';
import styled from '@emotion/styled';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { MathematicsStyles } from './extensions/Mathematics';
import { FigureToolbarButton } from './extensions/toolbar/FigureToolbarButton';
import { Public } from '@mui/icons-material';
import { AddInternalLinkToolbarButton } from './extensions/toolbar/AddInternalLinkToolbarButton';
import AddTableToolbarButton from './extensions/toolbar/AddTableToolbarButton';
import TableBubbleMenu from './extensions/TableBubbleMenu';
import { AddAppToolbarButton } from './extensions/toolbar/AddAppToolbarButton';
import { AddChartToolbarButton } from './extensions/toolbar/AddChartToolbarButton';
import { FormulaToolbarButton } from './extensions/toolbar/FormulaToolbarButton';
import { SymbolsToolbarButton } from './extensions/toolbar/SymbolsToolbarButton';
import { AddStandardLinkToolbarButton } from './extensions/toolbar/AddStandardLinkToolbarButton';
import { TextBubbleMenu } from './extensions/TextBubbleMenu';
import { AttachmentBubbleMenu } from './extensions/AttachmentBubbleMenu';
import MenuSelectOrderedLists from './extensions/toolbar/MenuSelectOrderedLists';
import { useThreadsState } from '../sidebar/tiptapComments/context';
import { css } from '@emotion/react';
import { useGuideCmsContext } from '../GuideCmsContext';
import { theme } from '../../../theme';
import { AddAttachmentLinkToolbarButton } from './extensions/toolbar/AddAttachmentLinkToolbarButton';
import IndentParagraphToolbarButtons from './extensions/toolbar/IndentParagraphToolbarButtons';
import { PageBreakToolbarButton } from './extensions/toolbar/PageBreakToolbarButton';

const cmsToolbarTheme = createTheme(theme, {
  components: {
    'MuiTiptap-MenuButtonTooltip': {
      defaultProps: {
        disableInteractive: true,
      },
    },
  },
});

const StyledEditor = styled.div<{ numberOfColumns: number; showThreads: boolean; showUnresolvedThreads: boolean }>`
  .tiptap.ProseMirror {
    --comment: rgba(250, 204, 21, 0.4);
    --comment-contrast: #facc15;
    --link-color: #c2ecff;
    --link-standard: var(--link-color);
    --link-guide: var(--link-color);
    --link-citation: #fff3c4;
    --link-internal-heading: var(--link-color);
    --link-internal-imageFigure: var(--link-color);
    --link-internal-chartFigure: var(--link-color);
    --link-internal-tableFigure: var(--link-color);
    --link-internal-app: var(--link-color);

    --diff-insert: #e2ffe8;
    --diff-delete: #ffe2e2;

    white-space: pre-wrap !important;
    padding-left: ${(props) => props.numberOfColumns * 35 + 8}px;
    padding-right: 8px;
    font-size: 16px;
    ${MathematicsStyles}
    figure {
      margin: 16px 0;
    }
    h1,
    h2,
    h3 {
      margin-top: 10px;
      margin-bottom: 10px;
    }
    h1 {
      font-size: 24px;
    }
    h2 {
      font-size: 20px;
    }
    h3 {
      font-size: 18px;
    }
    p {
      margin-bottom: 7px;
      line-height: 1.3;
    }
    .page-break {
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 16px 0;
      padding: 8px 0;
      border-top: 2px dashed #ccc;
      border-bottom: 2px dashed #ccc;
      background-color: #f5f5f5;
      color: #666;
      font-size: 12px;
      cursor: pointer;
      user-select: none;
      &::before {
        content: 'Sideskift';
      }
      &:hover {
        background-color: #e0e0e0;
        border-color: #999;
      }
      &.ProseMirror-selectednode {
        background-color: #bbdefb;
        border-color: #1976d2;
      }
    }
    ul,
    ol {
      padding-left: 18px;
    }
    ol[type='I'] {
      list-style-type: upper-roman;
    }
    ol[type='1'] {
      list-style-type: decimal;
    }
    ol[type='a'] {
      list-style-type: lower-alpha;
    }
    .link {
      padding: 0px 1px;
    }
    .link-icon > svg {
      font-size: 1.25rem;
      vertical-align: text-bottom;
      color: ${theme.palette.text.secondary};
    }
    .link-standard {
      background-color: var(--link-standard);
    }
    .link-guide {
      background-color: var(--link-guide);
    }
    .link-citation {
      background-color: var(--link-citation);
      font-size: 0.85em;
      padding: 0 3px;
      cursor: default;
    }
    .link-citation--broken {
      background-color: #ffe2e2;
      color: #b00020;
      font-weight: 600;
      text-decoration: line-through;
      font-size: 0.9em;
      padding: 1px 4px;
      outline: 1px solid #b00020;
    }
    .link-internal-heading {
      background-color: var(--link-internal-heading);
      cursor: pointer;
    }
    .link-internal-imageFigure,
    .link-internal-chartFigure {
      background-color: var(--link-internal-imageFigure);
    }
    .link-internal-tableFigure {
      background-color: var(--link-internal-tableFigure);
    }
    .link-internal-app {
      background-color: var(--link-internal-app);
    }
    [data-diff-type='inline-insert'],
    [data-diff-type='inline-delete'],
    [data-diff-type='block-insert'],
    [data-diff-type='block-delete'] {
      &[data-diff-user-id]:not([data-diff-user-id='']) {
        cursor: pointer;
        position: relative;
        display: inline-block;
        &:hover:before {
          visibility: visible;
        }
        &:before {
          content: attr(data-diff-user-id);
          font-weight: 400;
          line-height: normal;
          visibility: hidden;
          background-color: #fff;
          border: 1px solid #000;
          color: #000;
          text-align: center;
          border-radius: 6px;
          font-size: 14px;
          padding: 3px;
          position: absolute;
          z-index: 1;
          top: 24px;
          left: 5px;
          white-space: nowrap;
        }
      }
      position: relative;
      &:has(.link) {
        * {
          background-color: inherit;
        }
      }
      &:has(.node-tableFigure),
      &:has(p) {
        display: block !important;
      }
    }

    [data-diff-type='inline-insert'],
    [data-diff-type='block-insert'] {
      color: #000;
      background-color: var(--diff-insert);
    }

    [data-diff-type='inline-delete'],
    [data-diff-type='block-delete'] {
      color: #000;
      background-color: var(--diff-delete);
      text-decoration: line-through;
    }

    // Special handling of tables
    // Hide diff marks on nested nodes
    tbody > div.diff,
    tr > div.diff {
      display: contents;
    }

    ${({ showThreads, showUnresolvedThreads }) =>
      showThreads &&
      css`
        /* Thread mark */
        .tiptap-thread,
        .tiptap-thread * {
          transition: all 0.2s cubic-bezier(0.65, 0.05, 0.36, 1);
        }
        .tiptap-thread[data-type='block'] {
          &:first-child {
            margin-top: 0;
          }

          > :not(p, h1, h2, h3, h4, h5, h6, ul, li) {
            outline-width: 2px;
            outline-color: transparent;
            outline-style: dashed;
          }
        }
        ${showUnresolvedThreads ? '.tiptap-thread[data-status-all="open"]' : '.tiptap-thread[data-status-all="closed"]'} {
           &[data-type='block'] {
        > :not(p, h1, h2, h3, h4, h5, h6, ul, li) {
          background-color: var(--comment);
          outline-color: var(--comment);
        }

        &:hover,
        &[data-state='selected'],
        &[data-state='hovered'] {
          > :not(p, h1, h2, h3, h4, h5, h6, ul, li) {
            background-color: var(--comment-contrast);
            outline-color: var(--comment-contrast);
          }
        }
      }

      &[data-type='inline'] {
        border-bottom: 2px dashed var(--comment-contrast);

        &:hover,
        &[data-state='selected'],
        &[data-state='hovered'] {
          background-color: var(--comment);
        }
      }
      `};

    // Hide block node actions on nested nodes
    *[class*='node-'] {
      *[class*='node-'] {
        .block-node-actions {
          display: none;
        }
        .block-node-content {
          margin-left: 0;
        }
      }
    }
    /* Reference candidate highlighting */
    .reference-candidate-figure,
    .reference-candidate-table,
    .reference-candidate-heading,
    .reference-candidate-guide,
    .reference-candidate-standard {
      --_reference-canidate-color: #ffaa00;
      background-color: color-mix(in srgb, var(--_reference-canidate-color) 20%, transparent);
      border-bottom: 2px solid var(--_reference-canidate-color);
      cursor: pointer;
    }

    .reference-candidate-figure:hover,
    .reference-candidate-table:hover,
    .reference-candidate-heading:hover .reference-candidate-guide:hover .reference-candidate-standard:hover {
      opacity: 0.8;
    }

    .non-breaking-space {
      background-color: rgba(200, 200, 200, 0.4);
      border-radius: 2px;
    }

    /* Words from the dictionary (DictionaryHighlight): nei-ord, and ja-ord/synonyms in use */
    .rejected-term {
      text-decoration: underline wavy #d32f2f;
      text-underline-offset: 3px;
      /* Draw through descenders (g, p, j) instead of breaking the line around them */
      text-decoration-skip-ink: none;
      cursor: pointer;
    }

    .dictionary-term {
      text-decoration: underline solid #2e7d32;
      text-underline-offset: 3px;
      /* Draw through descenders (g, p, j) instead of breaking the line around them */
      text-decoration-skip-ink: none;
      cursor: pointer;
    }
  }
`;

export const GuideEditor: FC<{ guideId: string; editor: Editor; menuComponent?: React.ReactNode }> = ({ guideId, editor, menuComponent }) => {
  const { isSidebarOpen, sidebarType } = useGuideCmsContext();
  const { showUnresolved } = useThreadsState();

  // Subscribe to editor state changes so toolbar buttons and bubble menus
  // re-render when selection, content, or editability changes.
  // This replaces the old shouldRerenderOnTransaction: true approach,
  // scoping re-renders to GuideEditor instead of the entire GuideCms parent.
  const { isEditable } = useEditorState({
    editor,
    selector: ({ editor, transactionNumber }) => ({
      isEditable: editor.isEditable,
      transactionNumber,
    }),
  });

  const showThreads = isSidebarOpen && sidebarType === 'comments' && isEditable;
  const appBarHeight = 64;

  return (
    <StyledEditor numberOfColumns={0} showUnresolvedThreads={showUnresolved} showThreads={showThreads}>
      <RichTextEditorProvider editor={editor}>
        <RichTextField
          variant={isEditable ? 'outlined' : 'standard'}
          MenuBarProps={{ stickyOffset: appBarHeight, className: 'cms-menubar' }}
          controls={
            isEditable && (
              <ThemeProvider theme={cmsToolbarTheme}>
                <MenuControlsContainer>
                  {menuComponent && <div style={{ width: '100%' }}>{menuComponent}</div>}
                  <MenuButtonUndo />
                  <MenuButtonRedo />
                  <MenuDivider />
                  <MenuSelectHeading />
                  <MenuDivider />
                  <MenuButtonBold />
                  <MenuButtonItalic />
                  <MenuButtonUnderline />
                  <MenuButtonHighlightToggle />
                  <MenuDivider />
                  <MenuButtonSuperscript />
                  <MenuButtonSubscript />
                  <FormulaToolbarButton />
                  <SymbolsToolbarButton />
                  <MenuDivider />
                  <PageBreakToolbarButton />
                  <MenuSelectTextAlign />
                  <IndentParagraphToolbarButtons />
                  <MenuButtonBulletedList />
                  <MenuButtonOrderedList />
                  <MenuSelectOrderedLists />
                  <MenuDivider />
                  <MenuButtonEditLink IconComponent={Public} tooltipLabel="Ekstern lenke" />
                  <AddStandardLinkToolbarButton />
                  <AddGuideLinkToolbarButton guideId={guideId} />
                  <AddCitationLinkToolbarButton />
                  <AddInternalLinkToolbarButton />
                  <AddAttachmentLinkToolbarButton />
                  <MenuDivider />
                  <FigureToolbarButton />
                  <AddAppToolbarButton />
                  <AddChartToolbarButton />
                  <AddTableToolbarButton />
                </MenuControlsContainer>
              </ThemeProvider>
            )
          }></RichTextField>

        <TableBubbleMenu placement="top-start" fallbackPlacements={['bottom-start']} />
        <LinkBubbleMenu />
        <AttachmentBubbleMenu />
        <TextBubbleMenu guideId={guideId} />
      </RichTextEditorProvider>
    </StyledEditor>
  );
};
